import { useEffect, useRef, useState } from 'react';
import { io, type Socket } from 'socket.io-client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { RunStatus, type ChatMessageDto } from '@aiai/shared';
import { qk } from '../api/queryKeys';
import { fetchActiveChatRun } from '../api';
import { getToken } from '../lib/token';
import { refreshAccessToken } from '../lib/api';
import { getActiveOrgId } from '../lib/activeOrg';
import type { SocketStatus } from './useIssueSocket';

const SOCKET_BASE = import.meta.env.VITE_API_URL ?? '';

/**
 * Subscribes to the `/chat` Socket.IO namespace for a single project's PM
 * chat. Invalidates the chat queries (session list + every session's
 * messages, both under the `qk.chat` root) on new activity and tracks which
 * conversation the PM is currently answering (`thinkingSessionId`).
 *
 * Adapted from `useIssueSocket`, with three chat-specific differences:
 *  - the subscribe ack is checked — a `{ ok: false }` (membership/access denied)
 *    flips `subscribeFailed` so the page can surface a quiet "live updates
 *    unavailable" note instead of silently going stale.
 *  - the thinking state is *seeded* on mount from any in-flight chat-lane run,
 *    so a viewer landing mid-turn sees the indicator before the first event.
 *  - `failedSessionId` exposes the session whose last turn ended
 *    non-succeeded, so the page can invite a retry. It clears on the next
 *    `message`.
 *
 * `thinkingSessionId`/`failedSessionId` may be the sentinel `'*'` when the
 * server did not name a session (a legacy in-flight run) — treat that as
 * "applies to whichever conversation is open".
 *
 * `onPmMessage` is called with every new PM message (used for opt-in desktop
 * notifications); it is kept in a ref so a new callback never resubscribes.
 */
export function useChatSocket(
  projectId: string | undefined,
  onPmMessage?: (message: ChatMessageDto) => void,
): {
  thinkingSessionId: string | null;
  status: SocketStatus;
  subscribeFailed: boolean;
  failedSessionId: string | null;
} {
  const [status, setStatus] = useState<SocketStatus>('connecting');
  const [thinkingSessionId, setThinkingSessionId] = useState<string | null>(null);
  const [subscribeFailed, setSubscribeFailed] = useState(false);
  const [failedSessionId, setFailedSessionId] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const onPmMessageRef = useRef(onPmMessage);
  onPmMessageRef.current = onPmMessage;

  // Seed the thinking state from an in-flight chat-lane run so a fresh mount
  // (or a page reload mid-turn) shows the indicator immediately.
  const activeChatRunQuery = useQuery({
    queryKey: [...qk.chat(projectId ?? ''), 'active-run'],
    queryFn: () => fetchActiveChatRun(projectId!),
    enabled: !!projectId,
  });
  const seededThinking = activeChatRunQuery.data
    ? (activeChatRunQuery.data.chatSessionId ?? '*')
    : null;

  useEffect(() => {
    if (!projectId) return;

    setSubscribeFailed(false);

    const socket: Socket = io(`${SOCKET_BASE}/chat`, {
      path: '/socket.io',
      transports: ['websocket'],
      auth: { token: getToken() },
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 500,
      reconnectionDelayMax: 8000,
    });

    setStatus('connecting');

    const subscribe = () => {
      setStatus('connected');
      socket.emit(
        'subscribe',
        { projectId, orgId: getActiveOrgId() },
        (ack: { ok: boolean }) => {
          setSubscribeFailed(!ack?.ok);
        },
      );
    };

    socket.on('connect', subscribe);
    socket.io.on('reconnect_attempt', () => setStatus('reconnecting'));
    socket.on('disconnect', (reason: string) => {
      setStatus('disconnected');
      if (reason === 'io server disconnect') {
        setStatus('reconnecting');
        void refreshAccessToken().then((newToken) => {
          if (newToken) {
            socket.auth = { token: newToken };
            socket.connect();
          } else {
            setStatus('disconnected');
          }
        });
      }
    });
    socket.on('connect_error', () =>
      setStatus((s) => (s === 'connected' ? 'reconnecting' : 'disconnected')),
    );

    socket.on('message', (payload?: { message?: ChatMessageDto }) => {
      setFailedSessionId(null);
      if (payload?.message?.role === 'pm') onPmMessageRef.current?.(payload.message);
      void queryClient.invalidateQueries({ queryKey: qk.chat(projectId) });
    });

    socket.on('pm-turn-started', (payload: { sessionId?: string | null }) => {
      setThinkingSessionId(payload?.sessionId ?? '*');
    });

    socket.on(
      'pm-turn-settled',
      (payload: { status?: RunStatus; sessionId?: string | null }) => {
        setThinkingSessionId(null);
        if (payload?.status && payload.status !== RunStatus.Succeeded) {
          setFailedSessionId(payload?.sessionId ?? '*');
        }
        void queryClient.invalidateQueries({ queryKey: qk.chat(projectId) });
      },
    );

    return () => {
      socket.emit('unsubscribe', { projectId });
      socket.removeAllListeners();
      socket.disconnect();
    };
  }, [projectId, queryClient]);

  return {
    thinkingSessionId: thinkingSessionId ?? seededThinking,
    status,
    subscribeFailed,
    failedSessionId,
  };
}
